import React from 'react';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Card, CardContent } from '../ui/Card';
import { NativeSelect } from '../ui/NativeSelect';
import { AspirationFilters, AspirationSort, AspirationCategory, AspirationStatus } from '@/types/aspirations';
import { Search, Filter, X, SortAsc, SortDesc } from 'lucide-react';

interface AspirationFiltersProps {
  filters: AspirationFilters;
  sort: AspirationSort;
  onFiltersChange: (filters: AspirationFilters) => void;
  onSortChange: (sort: AspirationSort) => void;
  onClearFilters: () => void;
  totalCount: number;
  filteredCount: number;
}

const categories: AspirationCategory[] = [
  'Career', 'Financial', 'Health', 'Education', 'Personal', 
  'Family', 'Travel', 'Social', 'Spiritual'
];

const statuses: AspirationStatus[] = ['not_started', 'in_progress', 'completed'];

const sortOptions = [
  { field: 'title', label: 'Title' },
  { field: 'category', label: 'Category' },
  { field: 'status', label: 'Status' },
  { field: 'priority', label: 'Priority' },
  { field: 'progress', label: 'Progress' },
  { field: 'deadline', label: 'Deadline' },
  { field: 'updatedAt', label: 'Updated' },
] as const;

export function AspirationFiltersComponent({
  filters,
  sort,
  onFiltersChange,
  onSortChange,
  onClearFilters,
  totalCount,
  filteredCount
}: AspirationFiltersProps) {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = React.useState(false);

  const handleSearchChange = (value: string) => {
    onFiltersChange({ ...filters, search: value || undefined });
  };

  const toggleCategory = (category: AspirationCategory) => {
    const currentCategories = filters.category || [];
    const newCategories = currentCategories.includes(category)
      ? currentCategories.filter(c => c !== category)
      : [...currentCategories, category];
    
    onFiltersChange({ 
      ...filters, 
      category: newCategories.length > 0 ? newCategories : undefined 
    });
  };


  const toggleStatus = (status: AspirationStatus) => {
    const currentStatuses = filters.status || [];
    const newStatuses = currentStatuses.includes(status)
      ? currentStatuses.filter(s => s !== status)
      : [...currentStatuses, status];
    
    onFiltersChange({ 
      ...filters, 
      status: newStatuses.length > 0 ? newStatuses : undefined 
    });
  };

  const handleSortChange = (field: AspirationSort['field']) => {
    if (sort.field === field) {
      onSortChange({ 
        field, 
        direction: sort.direction === 'asc' ? 'desc' : 'asc' 
      });
    } else {
      onSortChange({ field, direction: 'asc' });
    }
  };

  const hasActiveFilters = !!(
    filters.search || 
    filters.category?.length || 
    filters.status?.length
  );

  return (
    <Card>
      <CardContent className="filters-content">
        {/* Mobile: Compact toggle button */}
        <div className="filters-mobile">
          <div className="layout-row-between">
            <span className="text-muted-sm">
              Showing {filteredCount} of {totalCount} aspirations
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)}
              className="layout-row-gap"
            >
              <Filter className="icon-md" />
              Filters & Sort
              {hasActiveFilters && (
                <Badge variant="secondary" className="ml-1">
                  {[
                    filters.search ? 1 : 0,
                    filters.category?.length || 0,
                    filters.status?.length || 0
                  ].reduce((a, b) => a + b, 0)}
                </Badge>
              )}
            </Button>
          </div>
        </div>

        {/* Desktop: Always visible controls */}
        <div className="filters-desktop">
          {/* Search and Sort Row */}
          <div className="filters-row">
            {/* Search */}
            <div className="filters-search-container">
              <Search className="filters-search-icon" />
              <Input
                placeholder="Search aspirations..."
                value={filters.search || ''}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 pr-8"
              />
              {filters.search && (
                <button
                  type="button"
                  onClick={() => handleSearchChange('')}
                  className="clear-btn"
                  aria-label="Clear search"
                >
                  <X className="h-6 w-6 text-muted-foreground" />
                </button>
              )}
            </div>

            {/* Sort */}
            <div className="layout-row-gap">
              <NativeSelect
                value={sort.field}
                onChange={(e) => handleSortChange(e.target.value as AspirationSort['field'])}
              >
                {sortOptions.map(option => (
                  <option key={option.field} value={option.field}>
                    Sort by {option.label}
                  </option>
                ))}
              </NativeSelect>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSortChange({ ...sort, direction: sort.direction === 'asc' ? 'desc' : 'asc' })}
              >
                {sort.direction === 'asc' ? <SortAsc className="icon-md" /> : <SortDesc className="icon-md" />}
              </Button>
            </div>

            {/* Filter Toggle */}
            <Button
              variant="outline"
              onClick={() => setIsExpanded(!isExpanded)}
              className="layout-row-gap"
            >
              <Filter className="icon-md" />
              Filters
              {hasActiveFilters && (
                <Badge variant="secondary" className="ml-1">
                  {[
                    filters.search ? 1 : 0,
                    filters.category?.length || 0,
                    filters.status?.length || 0
                  ].reduce((a, b) => a + b, 0)}
                </Badge>
              )}
            </Button>
          </div>

          {/* Results Summary */}
          <div className="filters-summary">
            <span>
              Showing {filteredCount} of {totalCount} aspirations
            </span>
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClearFilters}
                className="text-xs"
              >
                <X className="icon-sm mr-1" />
                Clear filters
              </Button>
            )}
          </div>

          {/* Desktop Expanded Filters */}
          {isExpanded && (
            <div className="filters-expanded">
              {/* Categories */}
              <div className="filters-section">
                <label className="text-label">Categories</label>
                <div className="filters-buttons">
                  {categories.map(category => (
                    <button
                      key={category}
                      onClick={() => toggleCategory(category)}
                      className={`filters-btn ${
                        filters.category?.includes(category) ? 'filters-btn-active' : ''
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              {/* Statuses */}
              <div className="filters-section">
                <label className="text-label">Status</label>
                <div className="filters-buttons">
                  {statuses.map(status => (
                    <button
                      key={status}
                      onClick={() => toggleStatus(status)}
                      className={`filters-btn ${
                        filters.status?.includes(status) ? 'filters-btn-active' : ''
                      }`}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Mobile: Expanded filters */}
        {isMobileFiltersOpen && (
          <div className="filters-mobile filters-expanded">
            {/* Search */}
            <div className="filters-search-container">
              <Search className="filters-search-icon" />
              <Input
                placeholder="Search aspirations..."
                value={filters.search || ''}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-10 pr-8"
              />
              {filters.search && (
                <button
                  type="button"
                  onClick={() => handleSearchChange('')}
                  className="clear-btn"
                  aria-label="Clear search"
                >
                  <X className="h-6 w-6 text-muted-foreground" />
                </button>
              )}
            </div>

            {/* Sort */}
            <div className="filters-section">
              <label className="text-label">Sort</label>
              <div className="layout-row-gap">
                <NativeSelect
                  value={sort.field}
                  onChange={(e) => handleSortChange(e.target.value as AspirationSort['field'])}
                  className="flex-1"
                >
                  {sortOptions.map(option => (
                    <option key={option.field} value={option.field}>
                      Sort by {option.label}
                    </option>
                  ))}
                </NativeSelect>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onSortChange({ ...sort, direction: sort.direction === 'asc' ? 'desc' : 'asc' })}
                >
                  {sort.direction === 'asc' ? <SortAsc className="icon-md" /> : <SortDesc className="icon-md" />}
                </Button>
              </div>
            </div>

            {/* Categories */}
            <div className="filters-section">
              <label className="text-label">Categories</label>
              <div className="filters-buttons">
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => toggleCategory(category)}
                    className={`filters-btn ${
                      filters.category?.includes(category) ? 'filters-btn-active' : ''
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Statuses */}
            <div className="filters-section">
              <label className="text-label">Status</label>
              <div className="filters-buttons">
                {statuses.map(status => (
                  <button
                    key={status}
                    onClick={() => toggleStatus(status)}
                    className={`filters-btn ${
                      filters.status?.includes(status) ? 'filters-btn-active' : ''
                    }`}
                  >
                    {status.charAt(0).toUpperCase() + status.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <div className="pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClearFilters}
                  className="text-xs"
                >
                  <X className="icon-sm mr-1" />
                  Clear all filters
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}